import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

// Comparison slugs to list — must stay in sync with COMPARISON_CONFIG in
// ComparisonPage.tsx and COMPARISON_SLUGS in prerender.js.
const COMPARISONS = [
  { slug: "petpooja", i18nKey: "petpooja", icon: "fa-utensils" },
  { slug: "rentok", i18nKey: "rentok", icon: "fa-hotel" },
  { slug: "ezee", i18nKey: "ezee", icon: "fa-hotel" },
  { slug: "hotelogix", i18nKey: "hotelogix", icon: "fa-hotel" },
  { slug: "cloudbeds", i18nKey: "cloudbeds", icon: "fa-hotel" },
  { slug: "zenoti", i18nKey: "zenoti", icon: "fa-spa" },
  { slug: "mindbody", i18nKey: "mindbody", icon: "fa-dumbbell" },
  { slug: "selldo", i18nKey: "selldo", icon: "fa-home" },
  { slug: "practo", i18nKey: "practo", icon: "fa-hospital" },
  { slug: "teachmint", i18nKey: "teachmint", icon: "fa-graduation-cap" },
];

const cardStyles = `
  .compare-index-card {
    background: rgba(15, 20, 40, 0.6);
    border: 1px solid rgba(0,255,151,0.15);
    border-radius: 16px;
    padding: 32px;
    height: 100%;
    transition: all 0.3s ease;
    display: block;
    color: inherit;
    text-decoration: none;
  }
  .compare-index-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
    color: inherit;
    text-decoration: none;
  }
  .compare-index-card h3,
  .compare-index-card p {
    color: inherit;
  }
  .compare-index-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(0,255,151,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const ComparePage: React.FC = () => {
  const { t } = useTranslation();

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: COMPARISONS.map((c, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: `https://axoix.com/compare/${c.slug}`,
      name: `AXOIX vs ${t(`comparisonData.${c.i18nKey}.competitorName`)}`,
    })),
  };

  return (
    <Fragment>
      <SEO
        title="AXOIX Comparisons"
        description="Feature-by-feature comparisons of AXOIX against named competitors — Petpooja for restaurant POS, RentOk for PG/hostel management, and more. Honest gaps included."
        path="/compare"
        schema={itemListSchema}
      />
      <style>{cardStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <PageTitle pageTitle="Compare AXOIX" pagesub="Compare" />

            <section className="pt-60 pb-0">
              <div className="container text-center">
                <p className="opacity-75" style={{ fontSize: "1.1rem", maxWidth: 700, margin: "0 auto" }}>
                  Direct, repo-verified comparisons against the named tools businesses actually consider — feature-by-feature, honest gaps included.
                </p>
              </div>
            </section>

            <section className="pt-60 pb-100">
              <div className="container">
                <div className="row">
                  {COMPARISONS.map((c) => {
                    const competitorName = t(`comparisonData.${c.i18nKey}.competitorName`);
                    const category = t(`comparisonData.${c.i18nKey}.category`);
                    const definition = t(`comparisonData.${c.i18nKey}.definition`);
                    return (
                      <div className="col-lg-6 mb-30" key={c.slug}>
                        <Link to={`/compare/${c.slug}`} className="compare-index-card">
                          <div className="compare-index-icon">
                            <i className={`fas ${c.icon}`}></i>
                          </div>
                          <h3 className="mb-10">AXOIX vs {competitorName}</h3>
                          <p className="opacity-60 mb-15" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                            {category}
                          </p>
                          <p className="opacity-80 mb-0">{definition}</p>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ComparePage;
